import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Signature, CheckCircle2, ShieldCheck } from "lucide-react";
import { useState } from "react";
import type { LaudoWithDetails } from "@shared/schema";
import { laudoEmModoTeste } from "@shared/modo-teste";

interface AssingLaudoProps {
  laudo: LaudoWithDetails;
  onSign: () => Promise<void>;
  isLoading?: boolean;
}

export function AssinarlLaudo({
  laudo,
  onSign,
  isLoading = false,
}: AssingLaudoProps) {
  const [open, setOpen] = useState(false);

  const isTestMode = laudoEmModoTeste(laudo);
  const rt = laudo.responsavelTecnico;
  const rtAssinaturaUrl = (rt as any)?.assinaturaUrl as string | undefined;
  const hasRtSignature = isTestMode || !!rtAssinaturaUrl;

  const hasAllRespostas =
    !laudo.laudoPecas ||
    laudo.laudoPecas.length === 0 ||
    laudo.laudoPecas.every(
      (p) =>
        !p.respostas ||
        p.respostas.length === 0 ||
        p.respostas.every(
          (r) => r.resposta !== null && r.resposta !== undefined,
        ),
    );

  const hasAllDestinacoes =
    !laudo.laudoPecas ||
    laudo.laudoPecas.length === 0 ||
    laudo.laudoPecas.every((p) => !!p.destinacao);

  const canSign = hasRtSignature && hasAllRespostas && hasAllDestinacoes;

  const _tirarCrea = (v: string) => {
    return v.replace("CREA", "").replaceAll("-", "");
  };

  const getMissingItems = () => {
    const missing: string[] = [];
    if (!hasAllRespostas)
      missing.push("Todas as respostas das peças devem ser preenchidas");
    if (!hasAllDestinacoes)
      missing.push("Todas as peças devem ter destinação definida");
    if (!hasRtSignature)
      missing.push("Responsável Técnico não possui assinatura cadastrada");
    return missing;
  };

  const handleConfirm = async () => {
    await onSign();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={
            laudo.status === "assinado" || laudo.status === "finalizado"
          }
        >
          <Signature className="w-4 h-4 mr-1.5" />
          {laudo.assinaturaUrl ? "Reassinar" : "Assinar"} Laudo
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Signature className="w-5 h-5" />
            Assinar Laudo
          </DialogTitle>
          <DialogDescription>
            Confirme a assinatura do Responsável Técnico para este laudo
          </DialogDescription>
        </DialogHeader>

        {!rt ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Nenhum Responsável Técnico associado a este laudo.
            </AlertDescription>
          </Alert>
        ) : !canSign ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-semibold mb-2">Não é possível assinar:</p>
              <ul className="list-disc pl-5 space-y-1">
                {getMissingItems().map((item, i) => (
                  <li key={i} className="text-sm">
                    {item}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Ao confirmar, você está aplicando a assinatura do RT e atestando
                todas as informações contidas no laudo.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">
                Responsável Técnico:{" "}
                <span className="font-semibold">{rt.nome}</span>
              </p>
              {rt.crea && (
                <p className="text-sm text-slate-500">
                  CREA: {_tirarCrea(rt.crea)}
                </p>
              )}

              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Assinatura Manuscrita (Rúbrica)
                </p>
                <div className="border rounded-lg p-3 bg-slate-50 flex items-center justify-center min-h-[80px]">
                  <img
                    src={rtAssinaturaUrl}
                    alt="Assinatura do RT"
                    className="max-h-20 object-contain"
                  />
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-blue-50 border-blue-200 flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <div className="space-y-0.5">
                  <p className="text-xs font-medium text-blue-800">
                    Assinatura Digital
                  </p>
                  <p className="text-xs text-blue-700">
                    O sistema registrará data e hora da assinatura vinculadas ao
                    responsável técnico, garantindo rastreabilidade e validade
                    jurídica ao documento.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !canSign}>
            {isLoading ? (
              "Assinando..."
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Confirmar Assinatura
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
